import React, { useState, useEffect } from 'react';
import { Sparkles, CheckCircle2, RefreshCw, Cpu, Save, Sliders, Zap, Smile, Eye, Shield, Layers, Radio, Palette } from 'lucide-react';
import { LunaFace, LUNA_FACE_CATALOG, setGlobalActiveLunaFace } from './LunaFace';

interface LunaFaceHubProps {
  authHeader: () => Record<string, string>;
  onClose?: () => void;
  isInline?: boolean;
}

export const LunaFaceHub: React.FC<LunaFaceHubProps> = ({ authHeader, onClose, isInline = true }) => {
  const [activeFaceId, setActiveFaceId] = useState<string>('cyber_cat');
  const [themeColor, setThemeColor] = useState<string>('#06b6d4');
  const [glowIntensity, setGlowIntensity] = useState<number>(0.8);
  const [eyeStyle, setEyeStyle] = useState<string>('cyber');
  const [animationSpeed, setAnimationSpeed] = useState<number>(1.0);
  
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Test states for interactive previews
  const [testState, setTestState] = useState<'idle' | 'listening' | 'speaking'>('idle');

  // Fetch active global face setting from API
  const fetchFaceSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/luna/face', { headers: authHeader() });
      const data = await res.json();
      if (data.success && data.setting) {
        setActiveFaceId(data.setting.activeFaceId || 'cyber_cat');
        setThemeColor(data.setting.themeColor || '#06b6d4');
        setGlowIntensity(data.setting.glowIntensity ?? 0.8);
        setEyeStyle(data.setting.eyeStyle || 'cyber');
        setAnimationSpeed(data.setting.animationSpeed ?? 1.0);
        setGlobalActiveLunaFace(data.setting.activeFaceId || 'cyber_cat');
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to fetch global face settings' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFaceSettings();
  }, []);

  const handleActivateFace = async (faceId: string) => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch('/api/admin/luna/face', {
        method: 'PUT',
        headers: {
          ...authHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activeFaceId: faceId,
          themeColor,
          glowIntensity,
          eyeStyle,
          animationSpeed
        })
      });
      const data = await res.json();
      if (data.success) {
        setActiveFaceId(faceId);
        setGlobalActiveLunaFace(faceId);
        setMessage({ type: 'success', text: `Successfully activated '${faceId}' face globally across all Luna instances!` });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to activate face globally' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Error applying global face change' });
    }
    setSaving(false);
  };

  const activeFaceInfo = LUNA_FACE_CATALOG.find(f => f.id === activeFaceId) || LUNA_FACE_CATALOG[0];

  const content = (
    <div className={`bg-[#0b0f19] border border-cyan-500/30 rounded-2xl w-full flex flex-col overflow-hidden shadow-[0_0_80px_rgba(6,182,212,0.15)] ${isInline ? '' : 'max-w-6xl max-h-[92vh]'}`}>
      
      {/* Header Bar */}
      <div className="px-6 py-5 border-b border-cyan-500/20 bg-black/60 flex items-center justify-between shrink-0 flex-wrap gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.3)]">
            <Smile className="text-cyan-400" size={24} />
          </div>
          <div>
            <h2 className="text-lg font-black text-white tracking-wide flex items-center gap-2 font-mono">
              LUNA FACE & UI MODEL HUB
              <span className="px-2.5 py-0.5 rounded-full text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-mono font-bold animate-pulse flex items-center gap-1">
                <Radio size={10} /> LIVE GLOBAL SYNC
              </span>
            </h2>
            <p className="text-xs text-slate-400 font-mono">Choose and customize the active visual avatar face applied globally across all Luna models</p>
          </div>
        </div>

        {/* Live Active Status Badge */}
        <div className="px-4 py-2 rounded-xl bg-slate-900 border border-cyan-500/30 flex items-center gap-3">
          <span className="text-[10px] text-slate-400 uppercase font-mono font-black">Active Global Face:</span>
          <span className={`px-3 py-1 rounded-lg text-xs font-black font-mono tracking-wider ${activeFaceInfo.color} bg-black/60 border border-white/10 flex items-center gap-1.5`}>
            <Sparkles size={12} /> {activeFaceInfo.name}
          </span>
        </div>
      </div>

      {/* Message Banner */}
      {message && (
        <div className={`px-6 py-3 text-xs font-bold font-mono flex items-center justify-between ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border-b border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border-b border-rose-500/30'}`}>
          <span className="flex items-center gap-2">
            <CheckCircle2 size={14} />
            {message.text}
          </span>
          <button onClick={() => setMessage(null)} className="text-white/60 hover:text-white">✕</button>
        </div>
      )}

      {/* Main Content Stage */}
      <div className="flex-1 p-6 space-y-6 overflow-y-auto font-sans">
        
        {/* Interactive Preview State Controller */}
        <div className="bg-black/40 border border-white/10 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-300">
            <Sliders size={15} className="text-cyan-400" />
            <span className="font-bold">Interactive Test Controls:</span>
            <span className="text-slate-400">Test how faces animate in real-time</span>
          </div>

          <div className="flex items-center gap-2">
            {[
              { id: 'idle', label: 'Idle State' },
              { id: 'listening', label: 'Listening (Voice Input)' },
              { id: 'speaking', label: 'Speaking (Voice Output)' },
            ].map(st => (
              <button
                key={st.id}
                onClick={() => setTestState(st.id as any)}
                className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all ${
                  testState === st.id
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
                    : 'bg-white/5 border border-white/10 text-slate-400 hover:text-white'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── 5 LUNA FACE PRESET CATALOG GRID ───────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {LUNA_FACE_CATALOG.map((face) => {
            const isActive = face.id === activeFaceId;
            return (
              <div
                key={face.id}
                className={`rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col bg-gradient-to-b ${face.bgGradient} ${
                  isActive
                    ? 'border-cyan-400 shadow-[0_0_30px_rgba(6,182,212,0.3)] ring-1 ring-cyan-400/50 scale-[1.01]'
                    : 'border-white/10 hover:border-white/20 hover:scale-[1.005]'
                }`}
              >
                {/* Card Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between bg-black/40">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase font-mono bg-white/10 ${face.color}`}>
                      {face.badge}
                    </span>
                    <h3 className="text-sm font-black text-white font-mono tracking-tight">{face.name}</h3>
                  </div>

                  {isActive && (
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-black font-mono flex items-center gap-1 shadow-sm">
                      <CheckCircle2 size={10} /> ACTIVE
                    </span>
                  )}
                </div>

                {/* Live Preview Box */}
                <div className="p-6 flex flex-col items-center justify-center bg-black/50 border-b border-white/5 relative min-h-[220px]">
                  <div className={`p-4 rounded-3xl bg-black/60 border ${face.borderColor} shadow-[0_0_35px_${face.glowColor}] transition-transform duration-300 hover:scale-105 flex items-center justify-center`}>
                    <LunaFace
                      faceId={face.id}
                      isListening={testState === 'listening'}
                      isSpeaking={testState === 'speaking'}
                      className={`w-32 h-44 ${face.color}`}
                    />
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono mt-3 font-medium">{face.tagline}</span>
                </div>

                {/* Description & Features */}
                <div className="p-4 flex-1 flex flex-col justify-between space-y-4">
                  <p className="text-xs text-slate-300 font-sans leading-relaxed">{face.description}</p>

                  {/* Activate Button */}
                  <button
                    onClick={() => handleActivateFace(face.id)}
                    disabled={saving || isActive}
                    className={`w-full py-2.5 rounded-xl font-mono text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-2 ${
                      isActive
                        ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 cursor-default'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)]'
                    }`}
                  >
                    {saving ? (
                      <Cpu className="animate-spin" size={14} />
                    ) : isActive ? (
                      <>
                        <CheckCircle2 size={14} /> GLOBALLY ACTIVE
                      </>
                    ) : (
                      <>
                        <Zap size={14} /> ACTIVATE GLOBALLY
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

      </div>

    </div>
  );

  if (isInline) return content;

  return (
    <div className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
      {content}
    </div>
  );
};

export default LunaFaceHub;
